import { all, spawn, call } from 'redux-saga/effects'
import { combineReducers } from '@reduxjs/toolkit'
import { appReducer as app, AppState } from './app'
import {
  authReducer as auth,
  AuthState,
  authWatcherSagas,
  facebookReducer as facebookUser,
  FacebookUserState,
  googleReducer as googleUser,
  GoogleUserState,
} from './auth'
import {
  lockerPlanReducer as lockerPlan,
  LockerPlanState,
  lockerPlanWatcherSagas,
} from './lockerPlan'
import {
  userReducer as user,
  UserState,
  userWatcherSagas,
  creditCardReducer as creditCard,
  CreditCardState,
} from './user'
import {
  spacesReducer as spaces,
  SpacesState,
  spaceWatcherSagas,
  spaceDetailReducer as spaceDetail,
  SpaceDetailState,
} from './space'
import {
  locationUnitReducer as locationUnit,
  LocationUnitState,
  locationUnitWatcherSagas,
} from './locationUnit'
import {
  lockersReducer as lockers,
  LockersState,
  lockerWatcherSagas,
  lockerDetailReducer as lockerDetail,
  LockerDetailState,
} from './locker'
import {
  bookingSuccessReducer as bookingSuccess,
  bookingWatcherSagas,
  bookingDetailReducer as bookingDetail,
  BookingDetailState,
  bookingInfoReducer as bookingInfo,
  BookingInfoState,
  CancelBookingState,
  successCancelBookingReducer as successCancelBooking,
} from './booking'
import {
  prefecturesReducer as prefectures,
  prefecturesWatcherSagas,
  PrefecturesState,
  stationsReducer as stations,
  StationsState,
  stationsWatcherSagas,
  facilitiesReducer as facilities,
  FacilitiesState,
  facilitiesWatcherSagas,
  locationDetailReducer as locationDetail,
  locationDetailWatcherSagas,
  LocationDetailState,
} from './prefecture'
import { BookingSuccessState } from './booking/reducers'

import {
  locationWatcherSagas,
  LocationDetailState as _LocationDetailState,
  locationDetailReducer as _locationDetail,
  LocationPopularAreasState,
  LocationPopularAreasReducer as locationPopularAreas,
  LocationRecommendedSpacesState,
  LocationRecommendedSpacesReducer as locationRecommendedSpaces,
  LocationCoinLockersState,
  LocationCoinLockersReducer as locationCoinLockers,

  // 駅
  LocationStationsState,
  LocationStationsReducer as locationStations,

  // 施設
  LocationFacilitiesState,
  LocationFacilitiesReducer as locationFacilities,

  // 旧コインロッカー
  LocationOldCoinLockersState,
  locationOldCoinLockersReducer as locationOldCoinLockers,
} from './locations'

import { CitiesState, CitiesReducer, citiesSagas } from './country'

export type StoreState = Readonly<{
  app: AppState
  auth: AuthState
  facebookUser: FacebookUserState
  googleUser: GoogleUserState
  lockerPlan: LockerPlanState
  user: UserState
  creditCard: CreditCardState
  spaces: SpacesState
  spaceDetail: SpaceDetailState
  lockers: LockersState
  lockerDetail: LockerDetailState
  bookingSuccess: BookingSuccessState
  bookingInfo: BookingInfoState
  bookingDetail: BookingDetailState
  cancelBookingStatus: CancelBookingState
  prefectures: PrefecturesState
  stations: StationsState
  facilities: FacilitiesState

  // locations
  locationDetail: LocationDetailState
  _locationDetail: _LocationDetailState
  locationPopularAreas: LocationPopularAreasState
  locationRecommendedSpaces: LocationRecommendedSpacesState
  locationCoinLockers: LocationCoinLockersState
  locationStations: LocationStationsState
  locationFacilities: LocationFacilitiesState
  locationOldCoinLockers: LocationOldCoinLockersState

  // country
  cities: CitiesState

  // locationUnit
  locationUnit: LocationUnitState
}>

export const rootReducer = combineReducers({
  app,
  auth,
  facebookUser,
  googleUser,
  lockerPlan,
  user,
  creditCard,
  spaces,
  spaceDetail,
  lockerDetail,
  bookingSuccess,
  bookingInfo,
  bookingDetail,
  successCancelBooking,
  prefectures,
  stations,
  facilities,
  locationDetail,
  locationUnit,

  _locationDetail,
  locationPopularAreas,
  locationRecommendedSpaces,
  locationCoinLockers,
  locationStations,
  locationFacilities,
  locationOldCoinLockers,

  // country
  cities: CitiesReducer,
})

// @see {@link https://redux-saga.js.org/docs/advanced/RootSaga.html}
export function* rootSaga() {
  const watchers = [
    ...lockerPlanWatcherSagas,
    ...authWatcherSagas,
    ...userWatcherSagas,
    ...spaceWatcherSagas,
    ...lockerWatcherSagas,
    ...bookingWatcherSagas,
    ...prefecturesWatcherSagas,
    ...stationsWatcherSagas,
    ...facilitiesWatcherSagas,
    ...locationDetailWatcherSagas,
    ...locationWatcherSagas,
    ...citiesSagas,
    ...locationUnitWatcherSagas,
  ]

  yield all(
    watchers.map((saga) =>
      // eslint-disable-next-line func-names
      spawn(function* () {
        while (true) {
          try {
            yield call(saga)
            break
          } catch (ex) {
            // eslint-disable-next-line no-console
          }
        }
      })
    )
  )
}
